// profileService.js
// Fetches profile and badge stats from Firestore.
// Mirrors XPCalculator.swift thresholds and BadgeManager stat accessors exactly.

import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

// ─── XP / Level thresholds ────────────────────────────────────────────────────
// Mirrors XPCalculator.levels exactly — DO NOT change without updating Swift too.

const LEVEL_THRESHOLDS = [
  { level: 1,  minXP: 0,         maxXP: 4_999    },
  { level: 2,  minXP: 5_000,     maxXP: 9_999    },
  { level: 3,  minXP: 10_000,    maxXP: 24_999   },
  { level: 4,  minXP: 25_000,    maxXP: 49_999   },
  { level: 5,  minXP: 50_000,    maxXP: 99_999   },
  { level: 6,  minXP: 100_000,   maxXP: 199_999  },
  { level: 7,  minXP: 200_000,   maxXP: 499_999  },
  { level: 8,  minXP: 500_000,   maxXP: 749_999  },
  { level: 9,  minXP: 750_000,   maxXP: 999_999  },
  { level: 10, minXP: 1_000_000, maxXP: null     },
];

export function levelFromXP(totalXP) {
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (totalXP >= LEVEL_THRESHOLDS[i].minXP) return LEVEL_THRESHOLDS[i].level;
  }
  return 1;
}

export function xpToNextLevel(totalXP) {
  const level = levelFromXP(totalXP);
  const t = LEVEL_THRESHOLDS[level - 1];
  if (!t || t.maxXP === null) return null;
  return t.maxXP - totalXP + 1;
}

export function levelProgressFraction(totalXP) {
  const level = levelFromXP(totalXP);
  const t = LEVEL_THRESHOLDS[level - 1];
  if (!t || t.maxXP === null) return 1;
  const span = t.maxXP - t.minXP;
  return Math.min(1, Math.max(0, (totalXP - t.minXP) / span));
}

export function badgeColorForLevel(level) {
  if (level <= 3)  return "rgba(255,255,255,0.75)";
  if (level <= 6)  return "#d4af37";
  if (level <= 9)  return "#66ccff";
  return "#d4f53c"; // app theme — level 10
}

// ─── Sport key mapping ────────────────────────────────────────────────────────

const SPORT_KEYS = {
  football:   "Football",
  baseball:   "Baseball",
  basketball: "Basketball",
  hockey:     "Hockey",
  soccer:     "Soccer",
};

// ─── Swift [Sport: Value] array decoder ──────────────────────────────────────
// Swift's Codable encodes [Sport: Int] dictionaries as flat alternating arrays:
// ["Baseball", 986, "Hockey", 907, ...] → { Baseball: 986, Hockey: 907, ... }

function parseSwiftDict(arr) {
  if (!arr || !Array.isArray(arr)) return {};
  const result = {};
  for (let i = 0; i < arr.length - 1; i += 2) {
    result[arr[i]] = arr[i + 1];
  }
  return result;
}

// ─── Load badge stats from Firestore ─────────────────────────────────────────
// Reads users/{uid}/appData/badgeStats → JSON-encoded "data" field.
// Field names mirror BadgeStats struct in Swift exactly.

export async function loadProfileStats(uid) {
  try {
    const snap = await getDoc(doc(db, "users", uid, "appData", "badgeStats"));
    if (!snap.exists()) return { profile: defaultProfile(), raw: {} };

    const raw = snap.data();
    console.log("📊 badgeStats raw keys:", Object.keys(raw));

    // BadgeStats is stored as JSON string in "data" field
    let stats = {};
    if (raw.data) {
      try {
        stats = JSON.parse(raw.data);
        console.log("📊 badgeStats parsed keys:", Object.keys(stats));
        console.log("📊 totalXP:", stats.totalXP, "totalDraftsAllTime:", stats.totalDraftsAllTime);
        console.log("📊 bestRatingBySport:", JSON.stringify(stats.bestRatingBySport));
        console.log("📊 totalDraftsBySport:", JSON.stringify(stats.totalDraftsBySport));
        console.log("📊 bestStreakBySport:", JSON.stringify(stats.bestStreakBySport));
      } catch (e) {
        console.warn("📊 JSON parse failed, using raw:", e);
        stats = raw;
      }
    } else {
      console.log("📊 No 'data' field — using raw document fields");
      stats = raw;
    }

    const totalXP = Math.round(stats.totalXP || 0);
    const level   = levelFromXP(totalXP);

    // Parse all Swift [Sport: Value] dictionaries from alternating arrays
    const bestRating         = parseSwiftDict(stats.bestRatingBySport);
    const bestStreak         = parseSwiftDict(stats.bestStreakBySport);
    const totalDrafts        = parseSwiftDict(stats.totalDraftsBySport);
    const bestDynasty        = parseSwiftDict(stats.bestDynastyRatingBySport);
    const versusRecords      = parseSwiftDict(stats.versusRecordBySport);
    const bestDailyDraft     = parseSwiftDict(stats.bestDailyDraftRankBySport);
    const bestMonthlyDraft   = parseSwiftDict(stats.bestMonthlyDraftRankBySport);
    const bestDailyStreak    = parseSwiftDict(stats.bestDailyStreakRankBySport);
    const bestMonthlyStreak  = parseSwiftDict(stats.bestMonthlyStreakRankBySport);
    const champDraft         = parseSwiftDict(stats.dailyDraftChampionWinsBySport);
    const champStreak        = parseSwiftDict(stats.dailyStreakChampionWinsBySport);

    const sportStats = {};
    Object.keys(SPORT_KEYS).forEach(sportId => {
      const key = SPORT_KEYS[sportId];
      sportStats[sportId] = {
        highScore:         bestRating[key]        || 0,
        highStreak:        bestStreak[key]         || 0,
        draftsCompleted:   totalDrafts[key]        || 0,
        dynastyRating:     bestDynasty[key]        || 0,
        versusRecord:      versusRecords[key]      || "",
        bestDailyDraft:    bestDailyDraft[key]     || null,
        bestMonthlyDraft:  bestMonthlyDraft[key]   || null,
        bestDailyStreak:   bestDailyStreak[key]    || null,
        bestMonthlyStreak: bestMonthlyStreak[key]  || null,
        champDraft:        champDraft[key]         || 0,
        champStreak:       champStreak[key]        || 0,
      };
    });

    // Compute top draft across all sports
    const topDraftAllTime = Math.max(0, ...Object.values(bestRating).filter(Number.isFinite));
    const topStreakAllTime = Math.max(0, ...[stats.bestStreakAllTime || 0,
      ...Object.values(bestStreak).filter(Number.isFinite)]);

    return { profile: {
      totalXP,
      level,
      progressFraction: levelProgressFraction(totalXP),
      xpToNext:          xpToNextLevel(totalXP),
      totalDrafts:       stats.totalDraftsAllTime    || 0,
      topDraftAllTime,
      bestStreak:        topStreakAllTime,
      bestChemistry:     stats.bestChemistryBonus    || 0,
      consecutiveDays:   stats.currentConsecutiveDays|| 0,
      knockoutWins:      stats.knockoutWins          || 0,
      sportStats,
    }, raw: stats };
  } catch (err) {
    console.error("profileService: failed to load badge stats", err);
    return { profile: defaultProfile(), raw: {} };
  }
}

function defaultProfile() {
  const sportStats = {};
  Object.keys(SPORT_KEYS).forEach(id => {
    sportStats[id] = {
      highScore: 0, highStreak: 0, draftsCompleted: 0,
      dynastyRating: 0, versusRecord: "",
      bestDailyDraft: null, bestMonthlyDraft: null,
      bestDailyStreak: null, bestMonthlyStreak: null,
      champDraft: 0, champStreak: 0,
    };
  });
  return {
    totalXP: 0, level: 1, progressFraction: 0, xpToNext: 4999,
    totalDrafts: 0, topDraftAllTime: 0, bestStreak: 0,
    bestChemistry: 0, consecutiveDays: 0, knockoutWins: 0,
    sportStats,
  };
}
